import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  forkJoin,
  from,
  map,
  merge,
  mergeMap,
  of,
  withLatestFrom
} from 'rxjs'
import {
  createOrderModel,
  getOppositeFromSecurity,
  validateOrderModel
} from '../../components/DepthOfMarket/helpers'
import { isOpenFinRuntime } from '../../containers/OpenFin/helpers'
import { getHub } from '../../helpers/hub'
import { createInvokeEpic } from '../../signalr/createInvokeEpic'
import { createStreamEpic } from '../../signalr/createStreamEpic'
import { getNextMessageId, splitCallByChunks } from '../helpers'
import { getListTradingWatchlistId } from '../listTrading/selectors'
import { addLogItem, addLogItems } from '../log/actions'
import { fetchSecuritiesByIds, RemoveGridAction } from '../securities/actions'
import {
  getSecurityOrderDataById,
  getSecurityStaticDataById
} from '../securities/selectors'
import { StagedOrder } from '../stagedOrders/types'
import { toggleDropdownState } from '../upload/actions'
import { getUserPreferences } from '../userPreferences/selectors'
import { getIcebergThreshold } from '../webSettings/selectors'
import { logError } from '../ws/actions'
import {
  addLastLookWindow,
  RemoveLastLookFromWindows
} from '../windows/actions'
import {
  AddOrUpdateOperatorOrdersAction,
  AddOrUpdateUserOrdersAction,
  clearMyOrders,
  CreateOrderAction,
  CreateOrderErrorAction,
  CreateOrdersAction,
  createTempOrder,
  fetchUserOrders,
  handleBulkErr,
  handleErr,
  loadResubmitOrders,
  ordersUpdated,
  setMyOrdersOpen,
  unsubscribeFetchUserOrders,
  updateOrdersValidations,
  UpdateOrdersValidationsAction,
  updateOrderValidation
} from './actions'
import {
  cancelOrderDefinition,
  cancelOrdersDefinition,
  getOperatorOrdersDefinition,
  getUserOrdersDefinition,
  submitOrderDefinition
} from './definition'
import {
  createOrdersFromResubmitOrders,
  getOrderTypeForResponse,
  orderModelToParams,
  showBest
} from './helpers'
import { getAcceptedOrders } from './selectors'
import { Order, ValidationResult } from './types'

type ErrorFriendlyStagedOrder = StagedOrder & { isin?: string }
const createOrdersEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<CreateOrdersAction>('order.createOrders'),
    mergeMap((action) => {
      const getSecurity = getSecurityOrderDataById(state$.value)
      const getStatic = getSecurityStaticDataById(state$.value)
      const icebergThreshold = getIcebergThreshold(state$.value)
      const { minimumTradeSize: traderPrefMinimum, maximumTradeSize } =
        getUserPreferences(state$.value)
      const [ordersCreationParams, orderValidations] =
        action.payload.params.reduce(
          ([createParams, validation], params) => {
            const security = getSecurity(params.securityId)
            const staticSecurity = getStatic(params.securityId)
            const isin = staticSecurity?.isin ?? security?.isin

            const orderModel = createOrderModel(
              params,
              params.orderType,
              staticSecurity?.product === 'PrinUSGovtOutright'
            )

            let error = ''

            if (security) {
              /*
                  We don't do any UST conversions/validation here because for that
                  we would need a string. My Orders does the conversion before
                  storing the price in the grid, but upload does NOT,
                  In order to support UST bulk upload, it needs to be added to
                  the Upload form.
                  We'll probably get this for free when we transition to 
                  OrderEntryModel everywhere.
               */
              const oppositeAmount =
                getOppositeFromSecurity(
                  security,
                  orderModel.type,
                  orderModel.amountType
                ) || NaN
              const minSize =
                traderPrefMinimum || staticSecurity?.minimumSize || 0
              error =
                validateOrderModel(
                  orderModel,
                  oppositeAmount,
                  icebergThreshold,
                  minSize,
                  maximumTradeSize || undefined
                ) ?? ''
              let formattedStack = ''
              if (error) {
                if (error.startsWith('Crossing')) {
                  formattedStack = showBest(security)
                }
                return [
                  createParams,
                  [
                    ...validation,
                    {
                      securityId: params.securityId,
                      orderType: params.orderType,
                      error,
                      isin,
                      formattedError:
                        'Error creating order: ' + error + ' ' + formattedStack
                    }
                  ]
                ]
              }
            }
            return [
              // TODO: this is a bit of a hack until we can transition to
              // OrderEntryModel for storing and sending values
              [
                ...createParams,
                {
                  ...params,
                  isin,
                  size: parseInt(orderModel.totalSize, 10),
                  totalSize: parseInt(orderModel.displaySize, 10)
                }
              ],
              [
                ...validation,
                {
                  securityId: params.securityId,
                  orderType: params.orderType,
                  error: undefined,
                  isin
                }
              ]
            ]
          },
          [[] as ErrorFriendlyStagedOrder[], [] as ValidationResult[]]
        )

      const bulkDate = new Date()
      const actions: Action[] = [
        updateOrdersValidations(orderValidations),
        addLogItems(
          action.payload.params.map(
            (ord) =>
              'Creating bulk order: security: ' +
              ord.securityId +
              ' price: ' +
              (ord.isSpreadOrder ? ord.spread : ord.price) +
              ' isSpread: ' +
              ord.isSpreadOrder +
              ' size: ' +
              ord.size +
              ' side: ' +
              ord.orderType +
              ' aon: ' +
              ord.allOrNone
          )
        )
      ]
      if (orderValidations.filter((v) => !!v.error).length) {
        // TODO: add gridIndex to createOrders action
        actions.push(toggleDropdownState(0, 'invalidUpload'))
      }
      const selUser = state$.value.users ? state$.value.users.selectedUser : 0
      return merge(
        from(actions),
        splitCallByChunks(ordersCreationParams, 2000, (chunk) =>
          forkJoin(
            chunk.map((orderCreationParams) =>
              getHub()
                .invoke(
                  'CreateOrder',
                  selUser,
                  orderCreationParams.securityId,
                  getOrderTypeForResponse(orderCreationParams.orderType),
                  orderCreationParams.isSpreadOrder
                    ? orderCreationParams.spread ?? 0
                    : orderCreationParams.price,
                  orderCreationParams.isSpreadOrder,
                  orderCreationParams.size,
                  orderCreationParams.allOrNone,
                  orderCreationParams.individualMin || 0,
                  orderCreationParams.custId,
                  { tob: false, limitPrice: 0, floorPrice: 0 },
                  '',
                  getNextMessageId(),
                  orderCreationParams.totalSize
                )
                .pipe(
                  catchError((err) =>
                    of(
                      handleErr(
                        err,
                        orderCreationParams.securityId,
                        orderCreationParams.orderType
                      )
                    )
                  )
                )
            )
          )
        ).pipe(
          concatMap((d: CreateOrderErrorAction[] /* probably*/) => {
            const arr2 = d.filter((elmt) => {
              return elmt.type === 'order.createOrderError'
            })
            const arr = arr2.map((e) => {
              const errOrder = ordersCreationParams.find(
                (o) => o.securityId === e.payload?.securityId
              )
              const errIsin = errOrder?.isin ?? e.payload.isin
              const err = `${e.payload?.err?.message}`
              const newErr = { ...e, payload: { ...e.payload } }
              if (err?.includes('InvalidDataException')) {
                newErr.payload.err.message =
                  'Internal error, no order was created (InvalidDataException)'
              }

              return { error: { ...newErr, isin: errIsin }, date: bulkDate }
            })
            const bulkActions: Action[] = [handleBulkErr(arr)]
            if (arr.length) {
              bulkActions.push(toggleDropdownState(0, 'invalidUpload'))
            }
            return from(bulkActions)
          })
        )
      )
    })
  )

const updateOrdersValidationsEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('order.updateValidations'),
    mergeMap((action: UpdateOrdersValidationsAction) => {
      const validationResults = action.payload
      const errors = validationResults
        .filter((vr) => vr.error !== undefined)
        .map((vr) => vr.formattedError!)
      return of(addLogItems(errors))
    })
  )
const createOrUpdateOrderOnSecurityEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('order.createOrder'),
    mergeMap((action: CreateOrderAction) => {
      const { securityId, order } = action.payload

      const params = orderModelToParams(order)

      const orderType = order.type

      const security = getSecurityOrderDataById(state$.value)(securityId)
      const getStatic = getSecurityStaticDataById(state$.value)(securityId)
      const icebergThreshold = getIcebergThreshold(state$.value)
      const { minimumTradeSize: traderPrefMinimum, maximumTradeSize } =
        getUserPreferences(state$.value)

      let error = ''
      if (security) {
        const oppositeAmount =
          getOppositeFromSecurity(security, order.type, order.amountType) || NaN
        const minSize = traderPrefMinimum || getStatic?.minimumSize || 0
        error =
          validateOrderModel(
            order,
            oppositeAmount,
            icebergThreshold,
            minSize,
            maximumTradeSize || undefined
          ) ?? ''
      }
      let formattedStack = ''
      if (error && security) {
        if (error.startsWith('Crossing')) {
          formattedStack = showBest(security)
        }
        const isin = getStatic?.isin ?? security.isin
        return of(
          addLogItem('Error creating order: ' + error + ' ' + formattedStack),
          updateOrderValidation({ securityId, orderType, error, isin })
        )
      }

      const messageId = getNextMessageId()
      const selUser = state$.value.users ? state$.value.users.selectedUser : 0
      return merge(
        of(updateOrderValidation({ securityId, orderType, error: undefined })),
        getHub()
          .invoke(
            'CreateOrder',
            selUser,
            securityId,
            getOrderTypeForResponse(orderType),
            params.onePrice,
            params.isSpreadOrder,
            params.serverSize,
            order.aon,
            params.individualMin,
            order.selectedBook || 0,
            params.tob,
            params.goodTill,
            messageId,
            params.displaySize
          )
          .pipe(
            map((transactionId: number) =>
              createTempOrder(
                transactionId,
                securityId,
                orderType,
                params.onePrice ?? 0,
                params.isSpreadOrder,
                params.clientSize,
                order.aon,
                params.individualMin,
                params.tob,
                undefined,
                params.clientSize === params.totalSize
                  ? undefined
                  : params.totalSize
              )
            ),
            catchError((err) =>
              of(handleErr(err, securityId, orderType), logError(err))
            )
          )
      )
    })
  )

const isLastLookOrder = (order: Order) =>
  order.status === 'waitingForConfirmation' && !!order.aggressorOrder

const lastLookOrdersEpic: Epic<Action> = (action$, state$) => {
  const seenAggressorIds = new Set<string>()

  const markSeenOnRemove$ = action$.pipe(
    ofType<RemoveLastLookFromWindows>('windows.removeLastLookFromWindows'),
    mergeMap((action) => {
      seenAggressorIds.add(action.payload.window.aggressorOrderId)
      return EMPTY
    })
  )

  const dispatchLastLook$ = action$.pipe(
    ofType<AddOrUpdateUserOrdersAction | AddOrUpdateOperatorOrdersAction>(
      'order.addOrUpdateUserOrders',
      'order.addOrUpdateOperatorOrders'
    ),
    withLatestFrom(state$),
    filter(([, state]) => getUserPreferences(state).lastLookInPopup),
    mergeMap(([action]) => {
      const newOrders = action.payload.filter(
        (
          order
        ): order is Order & {
          aggressorOrder: NonNullable<Order['aggressorOrder']>
        } =>
          isLastLookOrder(order) &&
          !seenAggressorIds.has(order.aggressorOrder!.id)
      )
      const actions: Action[] = []
      newOrders.forEach((order) => {
        seenAggressorIds.add(order.aggressorOrder.id)
        actions.push(
          addLastLookWindow({
            orderId: order.id,
            aggressorOrderId: order.aggressorOrder.id
          })
        )
      })
      return from(actions)
    })
  )

  return merge(markSeenOnRemove$, dispatchLastLook$)
}

const fetchSecuritiesForOrdersEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('order.addOrUpdateUserOrders'),
    mergeMap((action: AddOrUpdateUserOrdersAction) => {
      const getSecurity = getSecurityOrderDataById(state$.value)
      const ids = [
        ...new Set(action.payload.map((order) => order.securityId))
      ].filter((securityId) => getSecurity(securityId) === undefined)
      return ids.length > 0 ? of(fetchSecuritiesByIds(ids)) : EMPTY
    })
  )

const setSelectedUserEpic: Epic = (action$) =>
  action$.pipe(
    ofType('users.setSelectedUser'),
    map(() => {
      return unsubscribeFetchUserOrders()
    })
  )

const unsubscribeFetchUserOrdersEpic: Epic = (action$) =>
  action$.pipe(
    ofType('order.unsubscribeFetchUserOrders'),
    map(() => {
      return clearMyOrders()
    })
  )

const clearMyOrdersEpic: Epic = (action$) =>
  action$.pipe(
    ofType('order.clearMyOrders'),
    map(() => {
      return fetchUserOrders()
    })
  )

const getResubmitOrders: Epic = (action$) =>
  action$.pipe(
    ofType('order.fetchResubmitOrders'),
    mergeMap(() =>
      getHub()
        .invoke('GetResubmitOrders')
        .pipe(
          map((orders) =>
            loadResubmitOrders(createOrdersFromResubmitOrders(orders))
          ),
          catchError((err) => of(logError(err)))
        )
    )
  )

const resetMyOrdersOpenForGridEpic: Epic = (action$) =>
  action$.pipe(
    ofType<RemoveGridAction>('securities.removeGrid'),
    map((action) => setMyOrdersOpen(action.payload.gridIndex, false))
  )

const processedIds = new Set<string>()

const updatedOrdersEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<AddOrUpdateUserOrdersAction>('order.addOrUpdateUserOrders'),
    filter(() => {
      const isOpenFin = isOpenFinRuntime()
      return (
        !isOpenFin &&
        typeof getListTradingWatchlistId(state$.value) === 'number'
      )
    }),
    mergeMap((action) => {
      const getStaticData = getSecurityStaticDataById(state$.value)
      const acceptedOrders = getAcceptedOrders(state$.value)
      const orders = action.payload.filter((order) => {
        const matchingOrder = Object.values(acceptedOrders).find(
          (value) => value.id === order.corpOrderId
        )
        return (
          ['accepted', 'cancelled', 'rejected'].includes(order.status) &&
          !processedIds.has(order.id) &&
          (order.listTradingId || matchingOrder) &&
          getStaticData(order.securityId)
        )
      })
      orders.forEach((o) => processedIds.add(o.id))
      return from(orders.map(ordersUpdated))
    })
  )

export default combineEpics(
  createInvokeEpic('order.submit', submitOrderDefinition),
  createInvokeEpic('order.cancel', cancelOrderDefinition),
  createInvokeEpic('order.cancelOrders', cancelOrdersDefinition),
  createOrdersEpic,
  createOrUpdateOrderOnSecurityEpic,
  createStreamEpic('order.fetchUserOrders', getUserOrdersDefinition),
  createStreamEpic('order.fetchOperatorOrders', getOperatorOrdersDefinition),
  lastLookOrdersEpic,
  fetchSecuritiesForOrdersEpic,
  setSelectedUserEpic,
  unsubscribeFetchUserOrdersEpic,
  clearMyOrdersEpic,
  resetMyOrdersOpenForGridEpic,
  getResubmitOrders,
  updateOrdersValidationsEpic,
  updatedOrdersEpic
)
